import { useRef, useState, useContext, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
    ConfigProvider,
    Tabs,
    Select,
    Input,
    Button,
    Tag,
    Space,
    message,
    Popconfirm,
    Empty,
    Checkbox,
    Spin,
    Typography,
    Divider,
    Pagination,
} from "antd";
import {
    SearchOutlined,
    CheckCircleOutlined,
    UndoOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import logo from "../assests/baashyaam_logo.png";
import logo1 from "../assests/baashyaam_logo_white.png";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";

const { Text } = Typography;

const PAGE_SIZE = 10; // cards per page — keep in sync with backend EmployeeCardPagination.page_size
const BRAND_COLOR = "#0e497b";
const ID_COLOR ="#29469dff"
/* ------------------------------------------------------------------ */
/* Helper: normalize employee_photo into an absolute URL using backUrl */
/* ------------------------------------------------------------------ */
function resolvePhotoUrl(photo, backUrl) {
    if (!photo) return null;
    if (photo.startsWith("http://") || photo.startsWith("https://")) return photo;
    return `${backUrl}${photo.startsWith("/") ? "" : "/"}${photo}`;
}

/* ------------------------------------------------------------------ */
/* Single front/back ID card — identical visual style to EmpIdCard.jsx */
/* ------------------------------------------------------------------ */
function SingleEmployeeCard({ card, selected, onSelectToggle, onChangeStatus, backUrl }) {
    const cardRef = useRef();

    const employeePhotoUrl = resolvePhotoUrl(card.employee_photo, backUrl);

    const handleDownload = async () => {
        const element = cardRef.current;
        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`ID_Card_${card?.emp_code || "employee"}.pdf`);
    };

    return (
        <div
            style={{
                border: "1px solid #eee",
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
                background: "#fafafa",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <Space>
                    <Checkbox checked={selected} onChange={() => onSelectToggle(card.id)} />
                    <Text strong>{card.employee_name}</Text>
                    <Text type="secondary">({card.emp_code})</Text>
                    <Tag color={card.status === "ISSUED" ? "green" : "orange"}>{card.status}</Tag>
                </Space>

                <Space>
                    {card.status === "PENDING" ? (
                        <Popconfirm
                            title="Mark this card as Issued?"
                            onConfirm={() => onChangeStatus(card, "ISSUED")}
                        >
                            <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                                Issue
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Revert this card to Pending?"
                            onConfirm={() => onChangeStatus(card, "PENDING")}
                        >
                            <Button size="small" icon={<UndoOutlined />}>
                                Revert
                            </Button>
                        </Popconfirm>
                    )}
                    <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>
                        PDF
                    </Button>
                </Space>
            </div>

            <center>
                <div
                    ref={cardRef}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        gap: "20px",
                        marginTop: "10px",
                    }}
                >
                    {/* FRONT */}
                    <section
                        className="d-flex flex-column justify-content-between"
                        style={{
                            width: "250px",
                            height: "380px",
                            backgroundColor: "white",
                            borderRadius: "10px",
                            border: "1px solid gray",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: ID_COLOR,
                                borderTopLeftRadius: "10px",
                                borderTopRightRadius: "10px",
                                padding: "10px",
                                textAlign: "center",
                            }}
                        >
                            <img style={{ width: "210px", height: "80px" }} src={logo1} alt="Logo" />
                        </div>

                        <Text style={{ fontWeight: "bold" }}>{card.employee_name}</Text>

                        <div className="mt-2">
                            <img
                                style={{
                                    width: "110px",
                                    height: "130px",
                                    borderRadius: "10px",
                                    border: "1px solid black",
                                    objectFit: "cover",
                                }}
                                src={employeePhotoUrl}
                                alt="Employee"
                                crossOrigin="anonymous"
                            />
                        </div>

                        <div className="mt-2">
                            <Text style={{ fontWeight: "bold", fontSize: "12px" }}>
                                {card.designation_name}
                            </Text>
                            <br />
                            <Text style={{ fontWeight: "bold", fontSize: "12px" }}>
                                Employee Code : {card.emp_code}
                            </Text>
                        </div>

                        <div
                            style={{
                                backgroundColor: BRAND_COLOR,
                                width: "100%",
                                height: "20px",
                                borderBottomLeftRadius: "10px",
                                borderBottomRightRadius: "10px",
                            }}
                        ></div>
                    </section>

                    {/* BACK */}
                    <section
                        className="d-flex flex-column justify-content-between align-items-center"
                        style={{
                            width: "250px",
                            height: "380px",
                            backgroundColor: "white",
                            borderRadius: "10px",
                            border: "1px solid gray",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: BRAND_COLOR,
                                borderTopLeftRadius: "10px",
                                borderTopRightRadius: "10px",
                                padding: "10px",
                                width: "100%",
                                textAlign: "center",
                            }}
                        ></div>

                        <div style={{ padding: "10px", textAlign: "left", width: "100%" }}>
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "10px", marginBottom: "10px" }}>
                                D.O.J : {card.date_of_joining || "N/A"}
                            </Text>
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "10px", marginBottom: "10px" }}>
                                Blood Group : {card.blood_group || "N/A"}
                            </Text>
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "10px", marginBottom: "10px" }}>
                                Emergency : {card.emergency_contact_number || "N/A"}
                            </Text>
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "10px" }}>
                                Address : {card.address_for_communication || "N/A"}
                            </Text>
                        </div>

                        <div>
                            <img style={{ width: "200px", height: "60px" }} src={logo} alt="Logo" />
                        </div>

                        <div className="mb-2 px-1">
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "10px" }}>
                                BAASHYAAM CONSTRUCTIONS PVT. LTD
                            </Text>
                            <Text style={{ display: "block", fontWeight: "bold", fontSize: "8px" }}>
                                No. 87 GN Chetty Road, T.Nagar, Chennai - 600 017.
                            </Text>
                            <center><Text style={{ fontWeight: "bold", fontSize: "8px" }}>Ph: 044 4290 2345</Text></center>
                            <center><Text style={{ fontWeight: "bold", fontSize: "8px" }}>Email: info@bashyamgroup.com</Text></center>
                            <center><Text style={{ fontWeight: "bold", fontSize: "8px" }}>www.bashyamgroup.com</Text></center>
                            <hr />
                            <center>
                                <Text style={{ fontWeight: "bold", fontSize: "8px", color: BRAND_COLOR }}>
                                    Finders of this card may kindly return it to the issuing authority.
                                </Text>
                            </center>
                        </div>
                    </section>
                </div>
            </center>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Main manager: tabs + filters + paginated card gallery                */
/* ------------------------------------------------------------------ */
export default function EmpIdCard() {
    const { backUrl } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState("PENDING");
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState({ pending: 0, issued: 0 });

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [organisationFilter, setOrganisationFilter] = useState(undefined);
    const [departmentFilter, setDepartmentFilter] = useState(undefined);
    const [designationFilter, setDesignationFilter] = useState(undefined);

    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    const [selectedIds, setSelectedIds] = useState([]);

    // --- Pagination state (server-side) ---
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Filter dropdown options are fetched exactly once on mount — never re-fetched
    // on every filter change, so there's only ever a single round trip for each.
    const fetchOrganisations = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganisations(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch organisations:", err);
        }
    }, [backUrl]);

    const fetchDepartments = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/`);
            setDepartments(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    }, [backUrl]);

    const fetchDesignations = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/`);
            setDesignations(res.data.results || res.data);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    }, [backUrl]);

    const fetchCounts = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/employee-cards/status-counts/`);
            setCounts(res.data);
        } catch (err) {
            console.error("Failed to fetch counts:", err);
        }
    }, [backUrl]);

    const fetchCards = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                status: activeTab,
                page,
                page_size: PAGE_SIZE, // always 10 per page, single source of truth
            };
            if (appliedSearch) params.search = appliedSearch;
            if (organisationFilter) params.organisation = organisationFilter;
            if (departmentFilter) params.department = departmentFilter;
            if (designationFilter) params.designation = designationFilter;

            const res = await axios.get(`${backUrl}/api/emp/employee-cards/`, { params });

            // DRF PageNumberPagination shape: { count, next, previous, results }
            const data = res.data;
            if (Array.isArray(data)) {
                // Fallback in case pagination isn't active on the backend yet
                setCards(data);
                setTotalCount(data.length);
            } else {
                setCards(data.results || []);
                setTotalCount(data.count || 0);
            }
        } catch (err) {
            console.error("Failed to fetch employee cards:", err);
            message.error("Failed to load employee cards");
        } finally {
            setLoading(false);
        }
    }, [backUrl, activeTab, appliedSearch, organisationFilter, departmentFilter, designationFilter, page]);

    // Filter dropdown data — fetched once on mount only
    useEffect(() => {
        fetchOrganisations();
        fetchDepartments();
        fetchDesignations();
        fetchCounts();
    }, [fetchOrganisations, fetchDepartments, fetchDesignations, fetchCounts]);

    // Whenever tab/search/filters change, jump back to page 1
    useEffect(() => {
        setPage(1);
        setSelectedIds([]);
    }, [activeTab, appliedSearch, organisationFilter, departmentFilter, designationFilter]);

    // Fetch whenever page (or any filter, via the effect above resetting page) changes
    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    const handleSearch = () => setAppliedSearch(searchText.trim());
    const clearFilters = () => {
        setSearchText("");
        setAppliedSearch("");
        setOrganisationFilter(undefined);
        setDepartmentFilter(undefined);
        setDesignationFilter(undefined);
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Selection only ever applies to cards currently loaded on this page —
    // avoids ever having to pull the whole table into memory client-side.
    const selectAllVisible = () => setSelectedIds(cards.map((c) => c.id));
    const clearSelection = () => setSelectedIds([]);

    const changeStatus = async (card, newStatus) => {
        try {
            await axios.post(
                `${backUrl}/api/emp/employee-cards/${card.id}/change-status/`,
                { status: newStatus }
            );
            message.success(
                `${card.employee_name || card.emp_code} marked as ${newStatus === "ISSUED" ? "Issued" : "Pending"}`
            );
            fetchCounts();
            fetchCards();
        } catch (err) {
            console.error("Failed to change status:", err);
            message.error("Failed to update status");
        }
    };

    const bulkChangeStatus = async (newStatus) => {
        if (selectedIds.length === 0) return;
        try {
            await axios.post(`${backUrl}/api/emp/employee-cards/bulk-change-status/`, {
                ids: selectedIds,
                status: newStatus,
            });
            message.success(
                `${selectedIds.length} card(s) marked as ${newStatus === "ISSUED" ? "Issued" : "Pending"}`
            );
            setSelectedIds([]);
            fetchCounts();
            fetchCards();
        } catch (err) {
            console.error("Bulk update failed:", err);
            message.error("Bulk update failed");
        }
    };

    const tabItems = [
        { key: "PENDING", label: `Pending (${counts.pending})` },
        { key: "ISSUED", label: `Issued (${counts.issued})` },
    ];

    const hasActiveFilters =
        appliedSearch || organisationFilter || departmentFilter || designationFilter;

    return (
        <ConfigProvider theme={{ token: { colorPrimary: BRAND_COLOR, colorLink: BRAND_COLOR } }}>
            <div style={{ padding: 16 }}>
                <Tabs activeKey={activeTab} items={tabItems} onChange={setActiveTab} />

                <Space style={{ marginBottom: 16 }} wrap>
                    <Input
                        placeholder="Search by Employee ID / Code / Name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 260 }}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                    <Button type="primary" onClick={handleSearch}>
                        Search
                    </Button>

                    <Select
                        placeholder="Filter by Organisation"
                        allowClear
                        style={{ width: 200 }}
                        value={organisationFilter}
                        onChange={setOrganisationFilter}
                        options={organisations.map((o) => ({ label: o.name, value: o.id }))}
                    />

                    <Select
                        placeholder="Filter by Department"
                        allowClear
                        style={{ width: 200 }}
                        value={departmentFilter}
                        onChange={setDepartmentFilter}
                        options={departments.map((d) => ({ label: d.name, value: d.id }))}
                    />

                    <Select
                        placeholder="Filter by Designation"
                        allowClear
                        style={{ width: 200 }}
                        value={designationFilter}
                        onChange={setDesignationFilter}
                        options={designations.map((d) => ({ label: d.name, value: d.id }))}
                    />

                    {hasActiveFilters && (
                        <Button onClick={clearFilters}>Clear Filters</Button>
                    )}
                </Space>

                <Divider style={{ margin: "8px 0" }} />

                <Space style={{ marginBottom: 16 }} wrap>
                    <Button onClick={selectAllVisible} disabled={cards.length === 0}>
                        Select All On This Page ({cards.length})
                    </Button>
                    <Button onClick={clearSelection} disabled={selectedIds.length === 0}>
                        Clear Selection
                    </Button>

                    {selectedIds.length > 0 && (
                        <>
                            <Text>{selectedIds.length} selected</Text>
                            {activeTab === "PENDING" ? (
                                <Popconfirm
                                    title={`Mark ${selectedIds.length} card(s) as Issued?`}
                                    onConfirm={() => bulkChangeStatus("ISSUED")}
                                >
                                    <Button type="primary" icon={<CheckCircleOutlined />}>
                                        Bulk Mark Issued
                                    </Button>
                                </Popconfirm>
                            ) : (
                                <Popconfirm
                                    title={`Revert ${selectedIds.length} card(s) to Pending?`}
                                    onConfirm={() => bulkChangeStatus("PENDING")}
                                >
                                    <Button icon={<UndoOutlined />}>Bulk Revert to Pending</Button>
                                </Popconfirm>
                            )}
                        </>
                    )}
                </Space>

                {loading ? (
                    <div style={{ textAlign: "center", padding: 60 }}>
                        <Spin size="large" />
                    </div>
                ) : cards.length === 0 ? (
                    <Empty description={`No ${activeTab === "PENDING" ? "pending" : "issued"} cards found`} />
                ) : (
                    <>
                        {cards.map((card) => (
                            <SingleEmployeeCard
                                key={card.id}
                                card={card}
                                selected={selectedIds.includes(card.id)}
                                onSelectToggle={toggleSelect}
                                onChangeStatus={changeStatus}
                                backUrl={backUrl}
                            />
                        ))}

                        <div style={{ textAlign: "center", margin: "24px 0" }}>
                            <Pagination
                                current={page}
                                pageSize={PAGE_SIZE}
                                total={totalCount}
                                onChange={(nextPage) => setPage(nextPage)}
                                showSizeChanger={false}
                                showTotal={(total) => `${total} card(s) total`}
                            />
                        </div>
                    </>
                )}
            </div>
        </ConfigProvider>
    );
}